<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto focus:outline-none" tabindex="0">
        <form @submit.prevent="saveFile">
            <div class="max-w-6xl mx-auto sm:px-6 lg:px-8 px-5 pt-6 pb-2">
                <div class="flex items-center justify-between gap-4">
                    <div class="flex-1 min-w-0">
                        <h1 class="text-2xl font-semibold text-gray-900">Datei bearbeiten</h1>
                    </div>
                    <div class="flex items-center">
                        <router-link class="btn-stage-secondary mr-3" :to="fileManagerListPath">
                            {{ $t('Back') }}
                        </router-link>
                        <button
                            v-if="file.can_delete"
                            class="btn-stage-danger mr-3"
                            type="button"
                            @click="deleteModal = true"
                        >
                            Löschen
                        </button>
                        <button
                            v-if="file.can_write"
                            class="btn-stage-primary"
                            type="submit"
                        >
                            Speichern
                        </button>
                    </div>
                </div>
            </div>

            <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 pb-6">
                <div class="mt-6 space-y-6">
                    <loading :status="loading"/>

                    <div class="bg-white border border-bordergray-100 shadow rounded-md overflow-hidden">
                        <div class="px-4 py-5 sm:p-6 md:grid md:grid-cols-3 md:gap-x-6 md:gap-y-5">
                            <div class="md:col-span-1">
                                <h3 class="text-lg font-medium leading-6 text-gray-900">{{ file.can_write ? 'Datei' : 'Dateiname' }}</h3>
                                <p v-if="file.can_write" class="mt-1 text-sm leading-5 text-gray-500">Dateiname prüfen oder ändern.</p>
                            </div>
                            <div class="mt-5 md:mt-0 md:col-span-2">
                                <template v-if="file.can_write">
                                    <label class="block text-sm font-medium leading-5 text-gray-700" for="file-name">Dateiname</label>
                                    <input
                                        id="file-name"
                                        v-model="file.name"
                                        :disabled="!file.can_write"
                                        class="form-input text-sm mt-1 block w-full border-bordergray-100 rounded"
                                        required
                                    >
                                    <div class="mt-2 text-xs text-gray-500">
                                        Dateiname beim Upload am {{ uploadedAtDate }} um {{ uploadedAtTime }}: {{ file.original_name || '-' }}
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="text-lg text-gray-900 break-all">{{ displayFileName }}</div>
                                    <div v-if="hasShortDescription" class="mt-4">
                                        <div class="text-sm font-medium text-gray-700">Kurzbeschreibung</div>
                                        <div class="mt-1 text-sm text-gray-600 whitespace-pre-line">{{ file.short_description }}</div>
                                    </div>
                                </template>
                            </div>

                            <div v-if="file.can_write" class="md:col-start-2 md:col-span-2">
                                <label class="block text-sm font-medium leading-5 text-gray-700" for="file-short-description">Kurzbeschreibung</label>
                                <textarea
                                    id="file-short-description"
                                    v-model="file.short_description"
                                    :disabled="!file.can_write"
                                    class="form-textarea text-sm mt-1 w-full h-24 border-bordergray-100 rounded"
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    <div v-if="file.can_manage_permissions" class="bg-white border border-bordergray-100 shadow rounded-md md:grid md:grid-cols-3 md:gap-6 px-4 py-5 sm:p-6">
                        <div class="md:col-span-1">
                            <h3 class="text-lg font-medium leading-6 text-gray-900">Datei teilen</h3>
                            <p class="mt-1 text-sm leading-5 text-gray-500">Datei mit weiteren Kunden Ihres Unternehmens teilen.</p>
                        </div>
                        <div class="mt-5 md:mt-0 md:col-span-2">
                            <h4 class="text-sm font-medium text-gray-700 mb-2">Datei teilen</h4>
                            <div class="border border-bordergray-100 rounded max-h-56 overflow-y-auto">
                                <label v-for="customer in customers" :key="'customer-share-' + customer.id" class="flex items-center justify-between px-3 py-2 border-b border-bordergray-100 last:border-b-0">
                                    <span class="flex items-center text-sm text-gray-800">
                                        <img
                                            :src="getUserAvatar(customer)"
                                            :alt="customer ? customer.name : 'Avatar'"
                                            class="h-6 w-6 rounded-full object-cover mr-2"
                                        >
                                        {{ customer.name }}
                                    </span>
                                    <span class="inline-flex items-center text-sm">
                                        <span class="mr-2 text-gray-500">lesen</span>
                                        <input v-model="getCustomerPermissionState(customer.id).read" class="form-checkbox h-4 w-4 text-gray-600 mr-4" type="checkbox" @change="handlePermissionReadChange(getCustomerPermissionState(customer.id))">
                                        <span class="mr-2 text-gray-500">schreiben</span>
                                        <input v-model="getCustomerPermissionState(customer.id).write" :disabled="!getCustomerPermissionState(customer.id).read" class="form-checkbox h-4 w-4 text-gray-600" type="checkbox" @change="handlePermissionWriteChange(getCustomerPermissionState(customer.id))">
                                    </span>
                                </label>
                                <div v-if="!customers.length" class="px-3 py-2 text-sm text-gray-500">
                                    Keine weiteren Kunden Ihres Unternehmens verfügbar.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </form>

        <confirm-dialog :open="deleteModal" title="Datei löschen" text="Soll die Datei wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden." confirm-label="Löschen" confirm-variant="danger" @close="deleteModal = false" @confirm="deleteFile" />
    </main>
</template>

<script>
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import {resolveReturnToPath} from "@/utils/list-route-state";

export default {
    components: {ConfirmDialog},
    name: "file-manager-customer-manage",
    metaInfo() {
        return {
            title: "Datei bearbeiten"
        }
    },
    data() {
        return {
            loading: true,
            deleteModal: false,
            file: {
                name: '',
                original_name: '',
                short_description: '',
                author: null,
                download: '#',
                can_write: false,
                can_manage_permissions: false,
                can_delete: false,
                updated_at: null,
                customer_permissions: [],
            },
            customers: [],
            customerMap: {},
        };
    },
    mounted() {
        this.fetchOptions();
        this.getFile();
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    computed: {
        fileManagerListPath() {
            return resolveReturnToPath(this.$route.query || {}, '/file-manager/list');
        },
        displayFileName() {
            return this.file.name || this.file.original_name || '-';
        },
        hasShortDescription() {
            return !!(this.file.short_description && String(this.file.short_description).trim() !== '');
        },
        uploadedAtDate() {
            if (!this.file || !this.file.created_at) {
                return '--.--.----';
            }
            return moment(this.file.created_at).locale(window.app.app_date_locale).format('DD.MM.YYYY');
        },
        uploadedAtTime() {
            if (!this.file || !this.file.created_at) {
                return '--:--';
            }
            return moment(this.file.created_at).locale(window.app.app_date_locale).format('HH:mm');
        },
    },
    methods: {
        fetchOptions() {
            const self = this;
            axios.get('api/file-manager/options').then(function (response) {
                self.customers = response.data.customers || [];
                self.customers.forEach((customer) => {
                    self.ensurePermissionState(self.customerMap, customer.id);
                });
                self.applyPermissionState();
            });
        },
        getFile() {
            const self = this;
            self.loading = true;
            axios.get('api/file-manager/' + self.$route.params.id).then(function (response) {
                self.file = response.data;
                self.applyPermissionState();
                self.loading = false;
            }).catch(function () {
                self.loading = false;
                self.$router.push('/file-manager/list');
            });
        },
        applyPermissionState() {
            const self = this;
            if (!self.file || !self.file.customer_permissions) {
                return;
            }

            Object.keys(self.customerMap).forEach((key) => {
                self.customerMap[key] = {read: false, write: false};
            });

            (self.file.customer_permissions || []).forEach((permission) => {
                const state = self.ensurePermissionState(self.customerMap, permission.id);
                state.read = true;
                state.write = !!permission.can_write;
            });
        },
        ensurePermissionState(map, id) {
            const key = Number(id);
            if (!map[key]) {
                map[key] = {read: false, write: false};
            }

            return map[key];
        },
        getCustomerPermissionState(id) {
            return this.ensurePermissionState(this.customerMap, id);
        },
        handlePermissionReadChange(state) {
            if (!state) {
                return;
            }

            if (!state.read) {
                state.write = false;
            }
        },
        handlePermissionWriteChange(state) {
            if (!state) {
                return;
            }

            if (state.write) {
                state.read = true;
            }
        },
        buildCustomerPermissions() {
            return Object.keys(this.customerMap).reduce((carry, key) => {
                const id = Number(key);
                const permission = this.customerMap[id];
                if (permission.read) {
                    carry.push({id: id, can_write: !!permission.write});
                }
                return carry;
            }, []);
        },
        saveFile() {
            const self = this;
            if (!self.file.can_write) {
                return;
            }

            self.loading = true;
            const payload = {
                name: self.file.name,
                short_description: self.file.short_description,
            };
            if (self.file.can_manage_permissions) {
                payload.customer_permissions = self.buildCustomerPermissions();
            }

            axios.put('api/file-manager/' + self.$route.params.id, payload).then(function () {
                self.loading = false;
                self.$notify({
                    title: self.$i18n.t('Success').toString(),
                    text: self.$i18n.t('Data updated correctly').toString(),
                    type: 'success'
                });
                self.getFile();
            }).catch(function () {
                self.loading = false;
            });
        },
        deleteFile() {
            const self = this;
            if (!self.file.can_delete) {
                return;
            }

            self.loading = true;
            axios.delete('api/file-manager/' + self.$route.params.id).then(function () {
                self.loading = false;
                self.$router.push('/file-manager/list');
            }).catch(function () {
                self.loading = false;
                self.deleteModal = false;
            });
        },
        getUserAvatar(user) {
            if (!user) {
                return '';
            }
            if (user.avatar && user.avatar !== 'gravatar') {
                return user.avatar;
            }
            if (user.gravatar) {
                return user.gravatar;
            }
            return '';
        },
    }
}
</script>
